import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KadmiLogo } from './KadmiLogo';
import { X, Send, MessageCircle, Paperclip, Camera, FileText, Copy, Check, Loader2, Image as ImageIcon, CheckCircle2, Pencil, Upload, Volume2, Pause as PauseIcon, Play as PlayIcon, Square as StopIcon, Mic, MicOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';

const V = '#7FB342';
const MAX_FREE_MSGS = 3;
const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_IMG_DIM = 1600;

const TypingDots = memo(() => (
  <div className="flex gap-1 items-center" data-testid="chatbot-typing-dots">
    {[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#7FB342', animationDelay: `${i*0.15}s` }}/>)}
  </div>
));
TypingDots.displayName = 'TypingDots';

// Compress image (canvas) and return base64 data URI (jpeg quality 0.72)
const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new window.Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_IMG_DIM || height > MAX_IMG_DIM) {
        const ratio = Math.min(MAX_IMG_DIM / width, MAX_IMG_DIM / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.72));
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const LANG_TO_LOCALE = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', ar: 'ar-SA', de: 'de-DE', it: 'it-IT', pt: 'pt-BR' };

const TTSInline = memo(({ text, lang, isDark }) => {
  const [state, setState] = useState('idle'); // 'idle' | 'playing' | 'paused'
  const utteranceRef = useRef(null);
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => () => {
    if (supported) window.speechSynthesis.cancel();
  }, [supported]);

  if (!supported || !text) return null;

  const locale = LANG_TO_LOCALE[lang] || 'fr-FR';

  const play = () => {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = locale;
    u.rate = 0.95;
    u.pitch = 1;
    u.onend = () => setState('idle');
    u.onerror = () => setState('idle');
    utteranceRef.current = u;
    window.speechSynthesis.speak(u);
    setState('playing');
  };
  const pause = () => { window.speechSynthesis.pause(); setState('paused'); };
  const resume = () => { window.speechSynthesis.resume(); setState('playing'); };
  const stop = () => { window.speechSynthesis.cancel(); setState('idle'); };

  const btnStyle = { background: 'transparent', border: `1px solid ${isDark ? 'rgba(30,91,224,0.3)' : 'rgba(30,91,224,0.2)'}`, color: V };

  if (state === 'idle') {
    return (
      <button onClick={play} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all hover:scale-105 shrink-0" style={btnStyle} data-testid="chatbot-tts-listen" aria-label="Écouter le résumé et l'explication">
        <Volume2 className="h-3 w-3" /> Écouter
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid="chatbot-tts-controls">
      <button onClick={state === 'paused' ? resume : pause} className="flex items-center justify-center w-6 h-6 rounded-full transition-all" style={btnStyle} aria-label={state === 'paused' ? 'Reprendre' : 'Pause'} data-testid="chatbot-tts-pause">
        {state === 'paused' ? <PlayIcon className="h-3 w-3" /> : <PauseIcon className="h-3 w-3" />}
      </button>
      <button onClick={stop} className="flex items-center justify-center w-6 h-6 rounded-full transition-all" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.2)', color: '#DC2626' }} aria-label="Arrêter" data-testid="chatbot-tts-stop">
        <StopIcon className="h-3 w-3" />
      </button>
    </div>
  );
});
TTSInline.displayName = 'TTSInline';

// Helper: pick color for success score
const scoreColor = (score) => {
  if (score >= 70) return { bg: '#ECFDF3', border: '#A6E9C4', text: '#067647', label: 'Élevée' };
  if (score >= 40) return { bg: '#FFF8EB', border: '#FFD699', text: '#B54708', label: 'Modérée' };
  return { bg: '#FEF3F2', border: '#FECACA', text: '#B42318', label: 'Faible' };
};

// Renders text with **bold** segments
const RichText = ({ text, color }) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <span style={{ color }}>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={i} style={{ color: '#1A1A2E', fontWeight: 700 }}>{p.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </span>
  );
};

const StructuredResponse = memo(({ data, isDark, onCopy, copied, onGeneratePdf, pdfLoading, editedLetter, onStartEdit, onSaveEdit, editing, onEditChange, lang, onFollowUp }) => {
  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const ts = isDark ? '#9998B8' : '#5B5B72';
  const cardBg = isDark ? 'rgba(127,179,66,0.08)' : '#F8FBF4';
  const cardBd = isDark ? 'rgba(127,179,66,0.25)' : '#D9E9C2';
  const currentLetter = editedLetter != null ? editedLetter : data.generated_letter;
  const ttsText = [data.summary && `Résumé : ${data.summary}`, data.explanation && `Analyse : ${data.explanation}`].filter(Boolean).join('. ');
  const score = typeof data.success_score === 'number' ? data.success_score : 0;
  const sc = scoreColor(score);
  const hasRisks = Array.isArray(data.risks) && data.risks.length > 0;
  const hasTimeline = Array.isArray(data.timeline) && data.timeline.length > 0;
  const hasLegal = Array.isArray(data.legal_references) && data.legal_references.length > 0;
  const followUps = Array.isArray(data.follow_up_questions) && data.follow_up_questions.length > 0
    ? data.follow_up_questions
    : ['Comprendre mes droits', 'Préparer mon dossier', 'Faire un recours'];

  return (
    <div className="rounded-2xl p-3.5 mb-2 space-y-3.5" style={{ background: cardBg, border: `1px solid ${cardBd}`, borderBottomLeftRadius: 4 }} data-testid="chatbot-structured-response">
      {/* Title */}
      {data.title && (
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-bold leading-snug" style={{ color: tp }} data-testid="chatbot-resp-title">{data.title}</p>
          {ttsText && <TTSInline text={ttsText} lang={lang} isDark={isDark} />}
        </div>
      )}

      {/* Summary */}
      {data.summary && (
        <div data-testid="chatbot-resp-summary">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: V }}>
            Résumé
          </p>
          <p className="text-sm leading-relaxed font-medium" style={{ color: tp }}>{data.summary}</p>
        </div>
      )}

      {/* Analysis */}
      {data.explanation && (
        <div data-testid="chatbot-resp-analysis">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: V }}>
            Analyse
          </p>
          <p className="text-sm leading-relaxed" style={{ color: ts }}>
            <RichText text={data.explanation} color={ts} />
          </p>
        </div>
      )}

      {/* Risks (red box) */}
      {hasRisks && (
        <div className="rounded-xl p-3" style={{ background: '#FEF3F2', border: '1px solid #FECACA' }} data-testid="chatbot-resp-risks">
          <p className="text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#B42318' }}>
            ⚠️ Si vous ne faites rien
          </p>
          <ul className="space-y-1">
            {data.risks.map((r, i) => (
              <li key={i} className="text-xs flex gap-2 leading-relaxed" style={{ color: '#7A271A' }}>
                <span className="shrink-0">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Timeline */}
      {hasTimeline && (
        <div data-testid="chatbot-resp-timeline">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: V }}>
            Plan d'action
          </p>
          <div className="space-y-2">
            {data.timeline.map((t, i) => (
              <div key={i} className="flex gap-2.5 items-start">
                <div className="shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold text-white" style={{ background: V, minWidth: 38, textAlign: 'center' }}>
                  {t.when || '—'}
                </div>
                <p className="text-xs leading-relaxed pt-0.5" style={{ color: tp }}>{t.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success score */}
      {score > 0 && (
        <div className="rounded-xl p-3 flex items-center justify-between gap-3" style={{ background: sc.bg, border: `1px solid ${sc.border}` }} data-testid="chatbot-resp-score">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: sc.text, opacity: 0.85 }}>
              Probabilité de réussite
            </p>
            <p className="text-base font-bold leading-tight mt-0.5" style={{ color: sc.text }}>
              {score}% · {sc.label}
            </p>
          </div>
          <div className="relative shrink-0" style={{ width: 44, height: 44 }}>
            <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              <circle cx="18" cy="18" r="15.5" fill="none" stroke={sc.border} strokeWidth="3" />
              <circle cx="18" cy="18" r="15.5" fill="none" stroke={sc.text} strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 97.39} 97.39`} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color: sc.text }}>
              {score}
            </div>
          </div>
        </div>
      )}

      {/* Legal base (optional) */}
      {hasLegal && (
        <div data-testid="chatbot-resp-legal">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: V }}>
            Base légale
          </p>
          <ul className="space-y-1">
            {data.legal_references.map((l, i) => (
              <li key={i} className="text-xs leading-relaxed" style={{ color: ts }}>
                <span className="font-semibold" style={{ color: tp }}>{l.label}</span>
                {l.description ? ` — ${l.description}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Generated letter */}
      {currentLetter && (
        <div data-testid="chatbot-resp-letter">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1.5" style={{ color: V }}>
            <Send className="h-3 w-3" /> Courrier officiel prêt
          </p>
          {editing ? (
            <textarea
              value={currentLetter}
              onChange={e => onEditChange(e.target.value)}
              className="w-full min-h-[140px] max-h-56 rounded-lg p-2.5 mb-2 text-xs leading-relaxed font-sans outline-none resize-y"
              style={{ background: '#fff', border: `1px solid ${V}`, color: tp }}
              data-testid="chatbot-letter-editor"
            />
          ) : (
            <div className="rounded-lg p-2.5 mb-2 max-h-40 overflow-y-auto" style={{ background: '#fff', border: `1px solid #E5E3F0` }}>
              <pre className="font-sans text-xs whitespace-pre-wrap leading-relaxed" style={{ color: ts }}>{currentLetter}</pre>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => onCopy(currentLetter)} className="h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 text-white transition-all" style={{ background: copied ? '#22C55E' : V }} data-testid="chatbot-copy-letter">
              {copied ? <><Check className="h-3.5 w-3.5" /> Copié</> : <><Copy className="h-3.5 w-3.5" /> Copier</>}
            </button>
            <button onClick={() => (editing ? onSaveEdit() : onStartEdit())} className="h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" style={{ background: editing ? V : 'transparent', color: editing ? '#fff' : tp, border: `1px solid ${editing ? V : '#D9E9C2'}` }} data-testid="chatbot-edit-letter">
              {editing ? <><Check className="h-3.5 w-3.5" /> OK</> : <><Pencil className="h-3.5 w-3.5" /> Modifier</>}
            </button>
            <button onClick={() => onGeneratePdf(currentLetter)} disabled={pdfLoading} className="h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" style={{ background: 'transparent', border: `1px solid #D9E9C2`, color: tp }} data-testid="chatbot-pdf-letter">
              {pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileText className="h-3.5 w-3.5" /> PDF</>}
            </button>
          </div>
        </div>
      )}

      {/* Follow-up suggestions */}
      {onFollowUp && followUps.length > 0 && (
        <div className="pt-1" data-testid="chatbot-resp-followups">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: ts }}>
            Pour aller plus loin
          </p>
          <div className="flex flex-wrap gap-1.5">
            {followUps.map((q, i) => (
              <button key={i} onClick={() => onFollowUp(q)} className="text-[11px] font-semibold px-3 py-1.5 rounded-full transition-all hover:scale-105" style={{ background: '#fff', border: `1px solid ${V}`, color: V }} data-testid={`chatbot-followup-${i}`}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
StructuredResponse.displayName = 'StructuredResponse';

const ChatBubble = memo(({ msg, isDark, onCopy, copiedIdx, idx, onGeneratePdf, pdfLoadingIdx, editingIdx, onStartEdit, onSaveEdit, onEditChange, editedLetters, lang, onFollowUp, isLastAssistant }) => {
  const isUser = msg.role === 'user';
  if (msg.role === 'assistant' && msg.structured) {
    return (
      <StructuredResponse
        data={msg.structured}
        isDark={isDark}
        lang={lang}
        onCopy={(text) => onCopy(text, idx)}
        copied={copiedIdx === idx}
        onGeneratePdf={(text) => onGeneratePdf(text, idx)}
        pdfLoading={pdfLoadingIdx === idx}
        editing={editingIdx === idx}
        editedLetter={editedLetters[idx]}
        onStartEdit={() => onStartEdit(idx, msg.structured.generated_letter)}
        onSaveEdit={() => onSaveEdit(idx)}
        onEditChange={(val) => onEditChange(idx, val)}
        onFollowUp={isLastAssistant ? onFollowUp : null}
      />
    );
  }
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap" style={{
        background: isUser ? V : (isDark ? 'rgba(127,179,66,0.10)' : '#F4F8EE'),
        color: isUser ? '#fff' : (isDark ? '#F0EFFF' : '#1A1A2E'),
        borderBottomRightRadius: isUser ? 4 : 16,
        borderBottomLeftRadius: isUser ? 16 : 4,
        border: isUser ? 'none' : (isDark ? '1px solid rgba(127,179,66,0.20)' : '1px solid #DEEBC8'),
      }}>
        {msg.attachment && (
          <div className="mb-2 rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.15)' }}>
            {msg.attachment.type === 'image' ? (
              <img src={msg.attachment.preview} alt="attach" className="max-h-32 w-auto" />
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 text-xs">
                <FileText className="h-4 w-4" /> {msg.attachment.name || 'Document PDF'}
              </div>
            )}
          </div>
        )}
        {msg.content}
        {!isUser && msg.streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle animate-pulse" style={{ background: '#7FB342' }} />}
      </div>
    </div>
  );
});
ChatBubble.displayName = 'ChatBubble';

const QuickReply = memo(({ text, onClick }) => (
  <button onClick={() => onClick(text)} className="text-xs font-medium px-3 py-1.5 rounded-full border transition-colors hover:bg-blue-50" style={{ borderColor: '#D4CFFF', color: V }}>{text}</button>
));
QuickReply.displayName = 'QuickReply';

const ChatBot = memo(() => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const lang = (i18n.language || 'fr').split('-')[0];
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2,8)}`);
  const [pendingFile, setPendingFile] = useState(null); // {type:'image'|'pdf', base64, preview, name}
  const [uploadError, setUploadError] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [pdfLoadingIdx, setPdfLoadingIdx] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editedLetters, setEditedLetters] = useState({}); // {[idx]: 'text'}
  const [isDragging, setIsDragging] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState('');
  const [disclaimerSeen, setDisclaimerSeen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try { return window.localStorage.getItem('kadmi_disclaimer_v1') === '1'; } catch { return true; }
  });
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const recognitionRef = useRef(null);
  const dragCounter = useRef(0);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const freeCount = useRef(0);
  const sendMessageRef = useRef(null);
  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const cardBg = isDark ? '#1A1A2E' : '#FFFFFF';
  const bd = isDark ? '#2E2E45' : '#E5E3F0';

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: 'assistant', content: "Bonjour 👋 Je suis votre Conseiller Kadmi.\n\nJe vous aide à comprendre vos courriers et à préparer vos démarches administratives.\n\nVous pouvez :\n• 📎 Joindre un PDF ou une photo de courrier\n• ✍️ Décrire votre situation\n• 🎤 Utiliser la dictée vocale\n\nComment puis-je vous aider ?" }]);
    }
    if (open && !disclaimerSeen) {
      setShowDisclaimer(true);
    }
  }, [open, msgs.length, disclaimerSeen]);

  const acceptDisclaimer = useCallback(() => {
    try { window.localStorage.setItem('kadmi_disclaimer_v1', '1'); } catch { /* noop */ }
    setDisclaimerSeen(true);
    setShowDisclaimer(false);
  }, []);

  // Listener global : permet à une autre page (ex: /domaine/:slug) d'ouvrir
  // le chatbot avec un message déjà prérempli dans le champ de saisie.
  useEffect(() => {
    const handler = (e) => {
      const prefill = e?.detail?.prefill;
      setOpen(true);
      if (typeof prefill === 'string' && prefill.trim()) {
        setInput(prefill);
      }
    };
    window.addEventListener('kadmi:open-chatbot', handler);
    return () => window.removeEventListener('kadmi:open-chatbot', handler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs, typing]);

  const processFile = useCallback(async (file) => {
    if (!file) return;
    setUploadError('');
    try {
      if (file.type === 'application/pdf') {
        if (file.size > MAX_PDF_SIZE) {
          setUploadError('PDF trop volumineux (max 10 Mo).');
          return;
        }
        const b64 = await fileToBase64(file);
        setPendingFile({ type: 'pdf', base64: b64, preview: null, name: file.name });
      } else if (file.type.startsWith('image/')) {
        const compressed = await compressImage(file);
        setPendingFile({ type: 'image', base64: compressed, preview: compressed, name: file.name });
      } else {
        setUploadError('Format non supporté. Utilisez une image ou un PDF.');
      }
    } catch {
      setUploadError('Impossible de lire le fichier.');
    }
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    await processFile(file);
    e.target.value = '';
  };

  // Drag & drop (desktop only)
  const handleDragEnter = useCallback((e) => {
    if (!isDesktop) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true);
    }
  }, [isDesktop]);

  const handleDragLeave = useCallback((e) => {
    if (!isDesktop) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  }, [isDesktop]);

  const handleDragOver = useCallback((e) => {
    if (!isDesktop) return;
    e.preventDefault();
    e.stopPropagation();
  }, [isDesktop]);

  const handleDrop = useCallback(async (e) => {
    if (!isDesktop) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) await processFile(file);
  }, [isDesktop, processFile]);

  // Speech-to-text (dictée vocale)
  const SpeechRecognitionCls = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechSupported = !!SpeechRecognitionCls;
  const recordingTimeoutRef = useRef(null);
  const finalTranscriptRef = useRef('');

  const toggleRecording = useCallback(() => {
    if (!speechSupported) {
      setMicError('La dictée vocale n\'est pas supportée par votre navigateur.');
      return;
    }
    // Stop if already recording — flush whatever was captured so far
    if (isRecording && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      return;
    }

    setMicError('');
    finalTranscriptRef.current = '';
    try {
      const recognition = new SpeechRecognitionCls();
      recognition.lang = LANG_TO_LOCALE[lang] || 'fr-FR';
      // iOS Safari bug: continuous=false often returns nothing silently.
      // Use continuous + interim results, then stop manually on silence or timeout.
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsRecording(true);
        // Safety timeout: auto-stop after 20s to prevent runaway recordings
        recordingTimeoutRef.current = setTimeout(() => {
          try { recognition.stop(); } catch { /* noop */ }
        }, 20000);
      };

      recognition.onend = () => {
        setIsRecording(false);
        recognitionRef.current = null;
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
          recordingTimeoutRef.current = null;
        }
        // Send whatever was captured
        const finalText = finalTranscriptRef.current.trim();
        if (finalText) {
          setInput(finalText);
          setTimeout(() => sendMessageRef.current?.(finalText), 200);
        } else if (!micError) {
          setMicError('Rien entendu. Parlez plus fort ou rapprochez le micro.');
        }
      };

      recognition.onerror = (event) => {
        setIsRecording(false);
        recognitionRef.current = null;
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
          recordingTimeoutRef.current = null;
        }
        const inIframe = typeof window !== 'undefined' && window.self !== window.top;
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          if (inIframe) {
            setMicError('Micro bloqué dans cette prévisualisation. Ouvrez kadmi-ai.com directement.');
          } else {
            setMicError('Micro refusé. Réglages > Safari > Microphone > Autoriser kadmi-ai.com.');
          }
        } else if (event.error === 'no-speech') {
          setMicError('Aucune parole détectée. Parlez plus fort et réessayez.');
        } else if (event.error === 'network') {
          setMicError('Connexion internet requise pour la dictée vocale.');
        } else if (event.error !== 'aborted') {
          setMicError('La dictée a échoué. Réessayez.');
        }
      };

      recognition.onresult = (event) => {
        // Accumulate final results + show interim in input for visual feedback
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0]?.transcript || '';
          if (event.results[i].isFinal) {
            finalTranscriptRef.current += transcript + ' ';
          } else {
            interim += transcript;
          }
        }
        // Live display: show accumulated final + current interim
        const display = (finalTranscriptRef.current + interim).trim();
        if (display) setInput(display);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      setIsRecording(false);
      recognitionRef.current = null;
      setMicError('Impossible de démarrer la dictée vocale.');
    }
  }, [speechSupported, isRecording, lang, SpeechRecognitionCls, micError]);

  // Cleanup recognition on unmount / close
  useEffect(() => () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* noop */ }
    }
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
    }
  }, []);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    const attach = pendingFile;
    if (!msg && !attach) return;
    setInput('');
    setPendingFile(null);
    setUploadError('');

    if (!isAuthenticated) {
      freeCount.current += 1;
      if (freeCount.current > MAX_FREE_MSGS) {
        setMsgs(prev => [...prev, { role: 'user', content: msg, attachment: attach }, { role: 'limit', content: '' }]);
        return;
      }
    }

    const userMsg = {
      role: 'user',
      content: msg || (attach ? (attach.type === 'image' ? '📷 Photo envoyée' : '📎 PDF envoyé') : ''),
      attachment: attach ? { type: attach.type, preview: attach.preview, name: attach.name } : null
    };
    setMsgs(prev => [...prev, userMsg]);
    setTyping(true);

    try {
      const payload = {
        message: msg,
        session_id: sessionId,
        history: msgs.filter(m => m.role !== 'limit' && !m.structured).slice(-10).map(m => ({ role: m.role, content: m.content }))
      };
      if (attach?.type === 'image') payload.image_base64 = attach.base64;
      if (attach?.type === 'pdf') payload.pdf_base64 = attach.base64;

      const res = await api.post('/chat/send', payload, { timeout: 60000 });
      const reply = res.data?.reply || '';
      const structured = res.data?.structured || null;

      if (structured) {
        setMsgs(prev => [...prev, { role: 'assistant', content: reply, structured }]);
        setTyping(false);
        return;
      }

      if (reply.includes('[GENERER_COURRIER]')) {
        const cleanReply = reply.replace('[GENERER_COURRIER]', '').trim();
        setMsgs(prev => [...prev, { role: 'assistant', content: cleanReply + '\n\nRedirection vers le générateur de courrier...' }]);
        setTyping(false);
        const allUserMsgs = [...msgs.filter(m => m.role === 'user').map(m => m.content), msg];
        const situation = allUserMsgs.join('\n');
        setTimeout(() => {
          navigate('/dashboard', { state: { prefillText: situation, fromChatbot: true } });
          setOpen(false);
        }, 1500);
        return;
      }

      setMsgs(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);
      setTyping(false);

      // Typewriter streaming effect (client-side) — gives the "ChatGPT" feel without backend SSE
      const totalLen = reply.length;
      const minStep = 1;
      // Aim for ~12-18 chars per tick; faster for very long replies
      const chunk = Math.max(minStep, Math.ceil(totalLen / 90));
      const tickMs = 18;
      let i = 0;
      const stream = () => {
        i = Math.min(totalLen, i + chunk);
        setMsgs(prev => {
          const copy = prev.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            copy[copy.length - 1] = { ...last, content: reply.slice(0, i), streaming: i < totalLen };
          }
          return copy;
        });
        if (i < totalLen) {
          setTimeout(stream, tickMs);
        }
      };
      stream();
      return;
    } catch {
      setMsgs(prev => [...prev, { role: 'assistant', content: 'Désolé, une erreur est survenue. Veuillez réessayer.' }]);
    }
    setTyping(false);
  }, [input, pendingFile, isAuthenticated, sessionId, msgs, navigate]);

  // Keep the latest sendMessage ref for async callbacks (speech recognition)
  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  const handleCopyLetter = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleStartEdit = (idx, initialText) => {
    setEditingIdx(idx);
    setEditedLetters(prev => (prev[idx] != null ? prev : { ...prev, [idx]: initialText || '' }));
  };
  const handleSaveEdit = () => setEditingIdx(null);
  const handleEditChange = (idx, value) => setEditedLetters(prev => ({ ...prev, [idx]: value }));

  const handleGeneratePdf = async (text, idx) => {
    setPdfLoadingIdx(idx);
    try {
      const res = await api.post('/letters/export-pdf', {
        letter_content: text,
        template_id: 'chatbot_generated',
        template_name: 'Courrier Kadmi',
        subject: 'Courrier',
        user_profile: {}
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `KadmiAi_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setUploadError('Le PDF n\'a pas pu être généré. Connectez-vous pour y accéder.');
    }
    setPdfLoadingIdx(null);
  };

  const quickReplies = ['Analyser mon courrier CAF', 'Contester une amende', 'Problème préfecture', 'Autre question'];

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-24 right-4 z-[9999] w-14 h-14 rounded-full flex items-center justify-center shadow-lg sm:bottom-6"
          style={{ background: V, boxShadow: '0 4px 20px rgba(127,179,66,0.45)', animation: 'chatPulse 2s ease-in-out 1' }} data-testid="chatbot-trigger">
          <MessageCircle className="h-6 w-6 text-white" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-[9999] w-full sm:w-[380px] flex flex-col"
          style={{ height: 'min(85vh, 600px)', background: cardBg, borderRadius: '20px 20px 0 0', boxShadow: '0 -4px 30px rgba(0,0,0,0.15)', border: `1px solid ${bd}`, backdropFilter: 'blur(12px)' }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          data-testid="chatbot-window"
        >
          {/* Drag & drop overlay (desktop only) */}
          {isDragging && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[20px_20px_0_0] pointer-events-none" style={{ background: isDark ? 'rgba(18,18,38,0.85)' : 'rgba(245,243,255,0.9)', backdropFilter: 'blur(6px)', border: `2px dashed ${V}` }} data-testid="chatbot-drop-overlay">
              <div className="text-center px-6">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center" style={{ background: `${V}20`, border: `1.5px solid ${V}` }}>
                  <Upload className="h-6 w-6" style={{ color: V }} />
                </div>
                <p className="text-sm font-semibold" style={{ color: tp }}>Déposez votre document ici</p>
                <p className="text-[11px] mt-1" style={{ color: isDark ? '#9998B8' : '#6B6B8A' }}>PDF ou image — max 10 Mo</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${bd}`, borderRadius: '20px 20px 0 0' }}>
            <KadmiLogo size={36} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight" style={{ color: tp }} data-testid="chatbot-header-name">Conseiller Kadmi</p>
              <p className="text-[10px] leading-tight mt-0.5" style={{ color: isDark ? '#9998B8' : '#6B6B8A' }} data-testid="chatbot-header-subtitle">
                Assistant IA · Démarches administratives
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg" style={{ color: isDark ? '#666680' : '#9CA3AF' }} data-testid="chatbot-close">
              <X className="h-4 w-4"/>
            </button>
          </div>

          {/* Persistent legal disclaimer banner */}
          <div className="px-3 py-1.5 shrink-0 flex items-center gap-2" style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }} data-testid="chatbot-disclaimer-banner">
            <span className="text-[11px]" aria-hidden="true">⚖️</span>
            <p className="text-[10px] leading-snug flex-1" style={{ color: '#92400E' }}>
              Kadmi est un assistant d'information. Il ne remplace pas un avocat.{' '}
              <button
                type="button"
                onClick={() => setShowDisclaimer(true)}
                className="underline font-semibold"
                style={{ color: '#92400E' }}
                data-testid="chatbot-disclaimer-link"
              >
                En savoir plus
              </button>
            </p>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1" style={{ color: tp }}>
            {(() => {
              // Find last assistant index for follow-up display
              let lastAssistantIdx = -1;
              for (let i = msgs.length - 1; i >= 0; i--) {
                if (msgs[i].role === 'assistant') { lastAssistantIdx = i; break; }
              }
              return msgs.map((m, i) => m.role === 'limit' ? (
                <div key={i} className="rounded-xl p-4 text-center my-2" style={{ background: isDark ? 'rgba(127,179,66,0.10)' : '#F4F8EE', border: `1px solid ${isDark ? 'rgba(127,179,66,0.25)' : '#D9E9C2'}` }}>
                  <p className="text-sm font-semibold mb-1" style={{ color: tp }}>Pour continuer, créez votre compte gratuit</p>
                  <p className="text-xs mb-3" style={{ color: isDark ? '#9998B8' : '#6B6B8A' }}>C'est en 30 secondes</p>
                  <button onClick={() => navigate('/register')} className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold" style={{ background: V }}>Créer mon compte</button>
                </div>
              ) : (
                <React.Fragment key={i}>
                  <ChatBubble msg={m} idx={i} isDark={isDark} lang={lang} onCopy={handleCopyLetter} copiedIdx={copiedIdx} onGeneratePdf={handleGeneratePdf} pdfLoadingIdx={pdfLoadingIdx} editingIdx={editingIdx} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onEditChange={handleEditChange} editedLetters={editedLetters} onFollowUp={sendMessage} isLastAssistant={i === lastAssistantIdx} />
                  {/* Follow-up suggestions for plain text assistant messages (no structured) */}
                  {m.role === 'assistant' && !m.structured && !m.streaming && i === lastAssistantIdx && i > 0 && !typing && (
                    <div className="flex flex-wrap gap-1.5 px-2 mb-2 -mt-1" data-testid="chatbot-followup-bar">
                      {['Comprendre mes droits', 'Préparer mon dossier', 'Faire un recours'].map((q, qi) => (
                        <button key={qi} onClick={() => sendMessage(q)} className="text-[11px] font-semibold px-3 py-1.5 rounded-full transition-all hover:scale-105" style={{ background: '#fff', border: `1px solid ${V}`, color: V }} data-testid={`chatbot-text-followup-${qi}`}>
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ));
            })()}
            {typing && (
              <div className="flex items-center gap-2 px-2 py-1" data-testid="chatbot-analyzing">
                <div className="rounded-2xl px-3 py-2 inline-flex items-center gap-2" style={{ background: 'rgba(127,179,66,0.10)', border: '1px solid rgba(127,179,66,0.20)' }}>
                  <span className="text-xs font-medium" style={{ color: '#5A7F2B' }}>
                    {pendingFile || msgs[msgs.length - 1]?.attachment ? 'Conseiller Kadmi analyse' : 'Conseiller Kadmi rédige'}
                  </span>
                  <TypingDots />
                </div>
              </div>
            )}
          </div>

          {msgs.length <= 1 && !typing && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {quickReplies.map(q => <QuickReply key={q} text={q} onClick={sendMessage}/>)}
            </div>
          )}

          {/* Attachment preview */}
          {pendingFile && (
            <div className="mx-3 mb-2 rounded-xl p-2 flex items-center gap-2" style={{ background: isDark ? 'rgba(30,91,224,0.1)' : 'rgba(30,91,224,0.06)', border: `1px solid ${bd}` }} data-testid="chatbot-attachment-preview">
              {pendingFile.type === 'image' ? (
                <img src={pendingFile.preview} alt="preview" className="h-10 w-10 rounded-lg object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: V }}>
                  <FileText className="h-5 w-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: tp }}>{pendingFile.name}</p>
                <p className="text-[10px]" style={{ color: isDark ? '#9998B8' : '#6B6B8A' }}>{pendingFile.type === 'image' ? 'Photo prête' : 'PDF prêt'} — cliquez sur envoyer</p>
              </div>
              <button onClick={() => setPendingFile(null)} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: isDark ? '#9998B8' : '#6B6B8A' }} data-testid="chatbot-remove-attachment">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {uploadError && (
            <div className="mx-3 mb-2 px-3 py-1.5 rounded-lg text-xs text-red-500" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.15)' }}>
              {uploadError}
            </div>
          )}

          {micError && (
            <div className="mx-3 mb-2 px-3 py-1.5 rounded-lg text-xs text-red-500 flex items-center gap-1.5" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.15)' }} data-testid="chatbot-mic-error">
              <MicOff className="h-3 w-3 shrink-0" /> {micError}
            </div>
          )}

          <div className="px-3 pb-3 pt-1 shrink-0">
            <div className="flex items-center gap-1.5 rounded-xl px-2" style={{ background: isDark ? '#252538' : '#F5F3FF', border: `1px solid ${bd}` }}>
              <button onClick={() => fileInputRef.current?.click()} disabled={typing} className="p-2 rounded-lg transition-colors hover:bg-black/5 disabled:opacity-30" title="Joindre un PDF ou une image" style={{ color: V }} data-testid="chatbot-attach-file">
                <Paperclip className="h-4 w-4"/>
              </button>
              <button onClick={() => cameraInputRef.current?.click()} disabled={typing} className="p-2 rounded-lg transition-colors hover:bg-black/5 disabled:opacity-30" title="Prendre une photo" style={{ color: V }} data-testid="chatbot-attach-camera">
                <Camera className="h-4 w-4"/>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFileSelect} data-testid="chatbot-file-input" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} data-testid="chatbot-camera-input" />
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder={isRecording ? 'Écoute en cours...' : (pendingFile ? 'Ajoutez un message (optionnel)...' : 'Écrivez votre message...')} className="flex-1 py-3 bg-transparent text-sm outline-none min-w-0" style={{ color: tp }} data-testid="chatbot-input"/>
              {speechSupported && (
                <button
                  onClick={toggleRecording}
                  disabled={typing}
                  className="p-2 rounded-lg transition-all disabled:opacity-30 shrink-0"
                  title={isRecording ? 'Arrêter la dictée' : 'Dictée vocale'}
                  aria-label={isRecording ? 'Arrêter la dictée vocale' : 'Démarrer la dictée vocale'}
                  style={{
                    color: isRecording ? '#fff' : V,
                    background: isRecording ? '#DC2626' : 'transparent',
                    animation: isRecording ? 'micPulse 1.2s ease-in-out infinite' : 'none'
                  }}
                  data-testid="chatbot-mic"
                >
                  <Mic className="h-4 w-4"/>
                </button>
              )}
              <button onClick={() => sendMessage()} disabled={(!input.trim() && !pendingFile) || typing} className="p-2 rounded-lg disabled:opacity-30 transition-transform hover:scale-105 shrink-0" style={{ color: V }} data-testid="chatbot-send">
                {typing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4"/>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legal disclaimer modal — shown on first open */}
      {showDisclaimer && (
        <div
          className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kadmi-disclaimer-title"
          data-testid="chatbot-disclaimer-modal"
        >
          <div
            className="w-full sm:max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <span className="text-xl" aria-hidden="true">⚖️</span>
              </div>
              <div className="flex-1">
                <h2 id="kadmi-disclaimer-title" className="text-base font-bold leading-tight" style={{ color: '#1A1A2E' }}>
                  Information importante
                </h2>
                <p className="text-[11px] mt-0.5" style={{ color: '#6B6B8A' }}>
                  À lire avant de commencer
                </p>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-2" style={{ color: '#1A1A2E' }}>
              <strong>Kadmi est un assistant d'information.</strong>
            </p>
            <p className="text-sm leading-relaxed mb-2" style={{ color: '#3F3F55' }}>
              Il ne remplace pas un avocat ni un professionnel du droit.
            </p>
            <p className="text-sm leading-relaxed mb-5" style={{ color: '#3F3F55' }}>
              Les informations fournies doivent être vérifiées auprès des autorités compétentes.
            </p>
            <button
              onClick={acceptDisclaimer}
              className="w-full h-11 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: V }}
              data-testid="chatbot-disclaimer-accept"
              autoFocus
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes chatPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}@keyframes micPulse{0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,0.6)}50%{box-shadow:0 0 0 6px rgba(220,38,38,0)}}`}</style>
    </>
  );
});

ChatBot.displayName = 'ChatBot';
export default ChatBot;
